/**
 * Persona groups for the sign-in flow picker. One seed per distinct home behavior,
 * not one per Excel inventory row.
 */

import { SEEDED_FLOWS, type LoginFlowDefinition } from "@/lib/login-flow"

export type SignInFlowPersonaGroup = "admin" | "faculty" | "one-site" | "student"

export interface SignInFlowGroupDefinition {
  id: SignInFlowPersonaGroup
  label: string
  icon: string
  flowIds: readonly string[]
}

export const SIGN_IN_FLOW_GROUPS: readonly SignInFlowGroupDefinition[] = [
  {
    id: "admin",
    label: "Admin",
    icon: "fa-light fa-user-shield",
    flowIds: [
      "role-super-admin",
      "role-super-admin-all-owned",
      "role-super-admin-all-request-access",
      "role-admin",
      "role-compliance-admin",
    ],
  },
  {
    id: "faculty",
    label: "Faculty",
    icon: "fa-light fa-chalkboard-user",
    flowIds: ["role-faculty", "role-faculty-admin"],
  },
  {
    id: "one-site",
    label: "Exxat One Site",
    icon: "fa-light fa-hospital",
    flowIds: ["role-one-site"],
  },
  {
    id: "student",
    label: "Student",
    icon: "fa-light fa-user-graduate",
    flowIds: [
      "role-student",
      "role-student-multi-program",
      "role-student-two-apps",
      "role-student-faculty",
      "role-student-admin",
      "role-student-faculty-admin",
      "role-student-split-persona",
    ],
  },
]

const GROUPED_ROLE_FLOW_IDS = new Set(
  SIGN_IN_FLOW_GROUPS.flatMap(group => group.flowIds),
)

export function signInFlowShapes(flows: readonly LoginFlowDefinition[]): LoginFlowDefinition[] {
  return flows.filter(flow => !flow.id.startsWith("role-"))
}

export function signInFlowUncategorizedRoles(
  flows: readonly LoginFlowDefinition[],
): LoginFlowDefinition[] {
  return flows.filter(
    flow => flow.id.startsWith("role-") && !GROUPED_ROLE_FLOW_IDS.has(flow.id),
  )
}

export function signInFlowGroupMembers(
  flows: readonly LoginFlowDefinition[],
  groupId: SignInFlowPersonaGroup,
): LoginFlowDefinition[] {
  const group = SIGN_IN_FLOW_GROUPS.find(entry => entry.id === groupId)
  if (!group) return []
  const byId = new Map(flows.map(flow => [flow.id, flow]))
  return group.flowIds
    .map(id => byId.get(id))
    .filter((flow): flow is LoginFlowDefinition => flow !== undefined)
}

export function signInFlowPersonaGroupForFlow(
  flowId: string,
): SignInFlowPersonaGroup | null {
  for (const group of SIGN_IN_FLOW_GROUPS) {
    if (group.flowIds.includes(flowId)) return group.id
  }
  return null
}

export function signInFlowMenuOrder(): string[] {
  const shapes = SEEDED_FLOWS.filter(flow => !flow.id.startsWith("role-")).map(flow => flow.id)
  const roles = SIGN_IN_FLOW_GROUPS.flatMap(group => group.flowIds)
  const seededRoleIds = new Set(
    SEEDED_FLOWS.filter(flow => flow.id.startsWith("role-")).map(flow => flow.id),
  )
  const uncategorized = SEEDED_FLOWS.filter(
    flow => flow.id.startsWith("role-") && !roles.includes(flow.id),
  ).map(flow => flow.id)
  return [...shapes, ...roles, ...uncategorized.filter(id => seededRoleIds.has(id))]
}
